import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { ChatMap, type ChatMapMarker, type MapMarkerRect } from 'tripPlanner/components/ChatMap'
import HoverPreview from 'tripPlanner/components/Chat/PlaceHoverPreview/HoverPreview'
import {
  MARKER_TYPE_EXPLORE,
  MARKER_TYPE_SUGGESTED_HOTEL,
  MARKER_TYPE_SUGGESTED_EXPERIENCE,
  type MarkerType,
} from 'tripPlanner/utils/mapMarkerTransforms'
import { HOVER_CLOSE_DELAY_MS } from 'tripPlanner/components/Chat/PlaceHoverPreview/constants'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { ChatExploreCategoryKey } from 'tripPlanner/config/exploreCategories'
import type { GeoPoint } from 'tripPlanner/types/placeAndDateTime'
import { getFitBoundsPaddingForDirection } from 'tripPlanner/components/Chat/chatMapConstants'
import { getLocaleDirection } from 'i18n/util/locale'

const FIT_PADDING = { top: 20, bottom: 20, left: 40, right: 60 }

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(130)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};

  /* Mapbox control corners are physical in both directions, so their margins are too */
  .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
    margin-block: ${rem(4)} 0;
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    margin-left: 0;
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    margin-right: ${rem(4)};
  }

  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
    margin-block: 0 ${rem(4)};
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    margin-left: 0;
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    margin-right: ${rem(4)};
  }
`

export interface DrawerListingMapItem {
  id: string
  name: string
  coordinates: GeoPoint
  kind: 'place' | 'experience' | 'hotel'
  categoryKey?: ChatExploreCategoryKey
}

const MARKER_TYPE_BY_KIND = {
  place: MARKER_TYPE_EXPLORE,
  experience: MARKER_TYPE_SUGGESTED_EXPERIENCE,
  hotel: MARKER_TYPE_SUGGESTED_HOTEL,
} satisfies Record<DrawerListingMapItem['kind'], MarkerType>

interface Props {
  items: Array<DrawerListingMapItem>
  coordinates: GeoPoint
  onSelectItem: (id: string) => void
  onViewOnMap?: () => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}

type HoveredItem = {
  id: string
  rect: MapMarkerRect
}

function DrawerListingMapPreview({
  items,
  coordinates,
  onSelectItem,
  onViewOnMap,
  itineraryData,
  onSendMessage,
}: Props) {
  const intl = useIntl()
  const isMobileOrTablet = useIsMobileOrTablet()
  const [hoveredItem, setHoveredItem] = useState<HoveredItem>()
  const fitBoundsPadding = useMemo(
    () => getFitBoundsPaddingForDirection(FIT_PADDING, getLocaleDirection(intl.locale)),
    [intl.locale],
  )
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const clearHoverTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) clearTimeout(hoverCloseTimeoutRef.current)
    hoverCloseTimeoutRef.current = undefined
  }, [])

  useEffect(() => clearHoverTimeout, [clearHoverTimeout])

  const clearHover = useCallback(() => {
    clearHoverTimeout()
    setHoveredItem(undefined)
  }, [clearHoverTimeout])

  const itemsById = useMemo(() => new Map(items.map(item => [item.id, item])), [items])
  const markers = useMemo<Array<ChatMapMarker>>(
    () => items.map(item => ({
      markerKey: item.id,
      lat: item.coordinates.lat,
      lng: item.coordinates.lng,
      name: item.name,
      label: item.name,
      markerType: MARKER_TYPE_BY_KIND[item.kind],
      categoryKey: item.categoryKey,
    })),
    [items],
  )
  // Refit when the visible pins change while keeping the Mapbox instance mounted.
  const markerSetKey = useMemo(
    () => items.map(item => `${item.id}:${item.coordinates.lat},${item.coordinates.lng}`).join('|'),
    [items],
  )
  const initialViewState = useMemo(() => ({
    latitude: coordinates.lat,
    longitude: coordinates.lng,
    zoom: 11,
  }), [coordinates.lat, coordinates.lng])

  const handleMarkerClick = useCallback((id: string) => {
    clearHover()
    if (itemsById.has(id)) onSelectItem(id)
  }, [clearHover, itemsById, onSelectItem])

  const handleMarkerMouseEnter = useCallback((id: string, rect: MapMarkerRect) => {
    clearHoverTimeout()
    setHoveredItem({ id, rect })
  }, [clearHoverTimeout])

  const handleMarkerMouseLeave = useCallback(() => {
    clearHoverTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredItem(undefined)
      hoverCloseTimeoutRef.current = undefined
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearHoverTimeout])

  const hoveredItemDetails = hoveredItem && itemsById.get(hoveredItem.id)
  const handlePreviewClick = useCallback(() => {
    if (hoveredItemDetails) onSelectItem(hoveredItemDetails.id)
    clearHover()
  }, [clearHover, hoveredItemDetails, onSelectItem])

  return (
    <MapWrapper>
      <ChatMap
        markers={markers}
        fitMarkersKey={markerSetKey}
        cameraMode="uncontrolled"
        initialViewState={initialViewState}
        fitBoundsDurationMs={0}
        fitBoundsPadding={fitBoundsPadding}
        scrollZoom
        showCompassControl={false}
        showStyleToggleControl={false}
        showFullscreenControl={!!onViewOnMap}
        onEnlarge={onViewOnMap}
        mapStyleMode="standard"
        onMarkerClick={handleMarkerClick}
        onMarkerMouseEnter={isMobileOrTablet ? undefined : handleMarkerMouseEnter}
        onMarkerMouseLeave={isMobileOrTablet ? undefined : handleMarkerMouseLeave}
        onMapMoveStart={clearHover}
      />
      {!isMobileOrTablet && hoveredItem && hoveredItemDetails && (
        hoveredItemDetails.kind === 'place' ? (
          <HoverPreview
            type="place"
            placeId={hoveredItemDetails.id}
            placeName={hoveredItemDetails.name}
            linkRect={hoveredItem.rect}
            onClick={handlePreviewClick}
            onClose={clearHover}
            onMouseEnter={clearHoverTimeout}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
          />
        ) : (
          <HoverPreview
            type="hotel"
            offerId={hoveredItemDetails.id}
            offerName={hoveredItemDetails.name}
            linkRect={hoveredItem.rect}
            onClick={handlePreviewClick}
            onClose={clearHover}
            onMouseEnter={clearHoverTimeout}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
          />
        )
      )}
    </MapWrapper>
  )
}

export default DrawerListingMapPreview
